import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useNavigateBack } from '../../hooks/useNavigateBack';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { createSpareSupplierEnquiryFromRequisition, duplicateSpareRequisition } from '../../services/mutations';
import { useSpareRequisition } from './useData';
import { createVesselSpareRequisitionsModuleConfig } from './vesselSpareRequisitionsModuleConfig';
import { moduleConfig as supplierEnquiryConfig } from '../../modules/spares/supplier-enquiries/supplierEnquiriesModuleConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { cn } from '../../lib/utils';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { Accordion, AccordionItem, Button, Card, ChevronLeftIcon, CountChip, EyeIcon, Label, Select, Textarea } from '../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { SpareItemDetailModal } from '../../modules/spares/spare-items/SpareItemDetailModal';
import { PipelineStatusCell } from '../../modules/spares/requisitions/requisitionsModuleConfig';

const formatRequisitionItemCell = (line: {
  itemVcode?: string | null;
  itemDescr?: string | null;
}) => {
  const v = (line.itemVcode ?? '').trim();
  const n = (line.itemDescr ?? '').trim();
  return [v, n].filter(Boolean).join(' - ');
};

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const vesselNumericId = useAccountVesselScope() ?? NaN;
  const moduleConfig = React.useMemo(
    () => createVesselSpareRequisitionsModuleConfig(Number.isFinite(vesselNumericId) ? vesselNumericId : 0).moduleConfig,
    [vesselNumericId],
  );
  const numericId = id ? Number(id) : NaN;
  const { item, isLoading, isError } = useSpareRequisition(Number.isFinite(numericId) ? numericId : undefined);
  const { colors } = useTheme();
  const { showError } = useNotification();
  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');
  const [copyBusy, setCopyBusy] = React.useState(false);
  const [enquiryOpen, setEnquiryOpen] = React.useState(false);
  const [enquirySupplierId, setEnquirySupplierId] = React.useState('');
  const [enquiryNotes, setEnquiryNotes] = React.useState('');
  const [enquiryBusy, setEnquiryBusy] = React.useState(false);
  const [viewItemId, setViewItemId] = React.useState<number | null>(null);
  const { data: suppliersRes } = useSWR<any>('/suppliers?page=1&limit=500', fetcher);
  const goBack = useNavigateBack(moduleConfig.module.route.list);
  const supplierModalOptions = React.useMemo(() => {
    const list = Array.isArray(suppliersRes) ? suppliersRes : suppliersRes?.data ?? [];
    return [
      { value: '', label: 'Select supplier' },
      ...(list as { id: number; name: string; code?: string | null }[]).map((s) => ({
        value: String(s.id),
        label: s.name,
      })),
    ];
  }, [suppliersRes]);

  if (!Number.isFinite(vesselNumericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid vessel.</p>;
  }

  if (!Number.isFinite(numericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid id.</p>;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center rounded-xl border border-dashed px-4 py-16', colors.border)}>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>Loading requisition…</p>
      </div>
    );
  }

  if (isError || !item) {
    return (
      <Card variant="outlined" className={cn('p-6 sm:p-8', colors.border, 'max-w-md')}>
        <p className={cn('text-sm', colors.textSecondary)}>This requisition was not found or you no longer have access.</p>
        <Button type="button" variant="ghost" className="mt-4" onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4" />
          Back to list
        </Button>
      </Card>
    );
  }

  const lineCount = (item.lines || []).length;
  const hasPurchaseOrder = (item.pipelineCounts?.orderCount ?? 0) > 0;

  return (
    <div className="space-y-6">
      <Card
        variant="outlined"
        className={cn('overflow-hidden border shadow-sm', colors.border)}
      >
        <div
          className={cn(
            'flex flex-col gap-4 border-b px-4 py-4 sm:px-6 sm:py-5',
            colors.border,
            'bg-gradient-to-b from-slate-50/80 to-white dark:from-slate-900/40 dark:to-slate-900/20',
          )}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn('-ml-2 gap-1.5 self-start font-medium', colors.text)}
                  onClick={goBack}
                >
                  <ChevronLeftIcon className="h-4 w-4 opacity-80" />
                  Back
                </Button>
                {item.saveStatus ? (
                  <span
                    className={cn(
                      'inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide',
                      item.saveStatus === 'DRAFT'
                        ? 'bg-amber-100 text-amber-950 dark:bg-amber-900/50 dark:text-amber-100'
                        : 'bg-emerald-100 text-emerald-950 dark:bg-emerald-900/45 dark:text-emerald-100',
                    )}
                  >
                    {item.saveStatus === 'DRAFT' ? 'Draft' : 'Saved'}
                  </span>
                ) : null}
                <PipelineStatusCell row={item} />
              </div>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={copyBusy}
                loading={copyBusy}
                onClick={async () => {
                  setCopyBusy(true);
                  try {
                    const created = (await duplicateSpareRequisition(item.id)) as { id?: number };
                    if (created?.id) navigate(moduleConfig.module.route.edit(created.id));
                  } finally {
                    setCopyBusy(false);
                  }
                }}
              >
                {copyBusy ? 'Copying…' : 'Copy as draft'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={!item.caseId || hasPurchaseOrder}
                title={
                  hasPurchaseOrder
                    ? 'Cannot create an enquiry while a purchase order exists for this requisition.'
                    : item.caseId
                      ? 'Create a supplier enquiry from this requisition'
                      : 'Link this requisition to a spare case before creating a supplier enquiry.'
                }
                onClick={() => {
                  setEnquirySupplierId('');
                  setEnquiryNotes('');
                  setEnquiryOpen(true);
                }}
              >
                Create enquiry
              </Button>
              <Link
                to={moduleConfig.module.route.edit(item.id)}
                className={cn(
                  'inline-flex h-9 min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition-all hover:opacity-95',
                  colors.primary,
                  colors.primaryText,
                )}
              >
                Edit
              </Link>
            </div>
          </div>
        </div>
      </Card>

      <Card variant="outlined" className={cn('p-4 shadow-sm sm:p-5', colors.border)}>
        <Accordion className="space-y-3">
          <AccordionItem
            title="Requisition details"
            description="Header, case link, and notes for this request."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mt-2 pt-1 sm:pt-0">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 text-sm md:grid-cols-12">
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Request date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.reqDate)}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Reference</dt>
                  <dd className={cn('mt-1 break-words font-medium tabular-nums', colors.text)}>{item.ref || ''}</dd>
                </div>
                {item.refN != null ? (
                  <div className="min-w-0 md:col-span-3">
                    <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Ref #</dt>
                    <dd className={cn('mt-1 tabular-nums', colors.text)}>{item.refN}</dd>
                  </div>
                ) : null}
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Spare case</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.spareCase?.id ? (
                      <Link
                        to={`/spares/spare-cases/${item.spareCase.id}/case-view`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.spareCase.ref || `Case #${item.spareCase.id}`}
                      </Link>
                    ) : item.caseId ? (
                      <span className="tabular-nums">#{item.caseId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>No case</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Vessel</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>{item.vessel?.vesselName || ''}</dd>
                </div>
                <div className="min-w-0 md:col-span-6 md:col-start-1">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Notes</dt>
                  <dd
                    className={cn('mt-1 min-h-[1.25rem] max-w-3xl whitespace-pre-wrap break-words', colors.text)}
                  >
                    {item.notes?.trim() ? item.notes : <span className={colors.textSecondary}>—</span>}
                  </dd>
                </div>
              </dl>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Line items"
            description="Store items, quantities, and line notes."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              lineCount > 0 ? (
                <CountChip className="mr-1.5">
                  {lineCount} line{lineCount === 1 ? '' : 's'}
                </CountChip>
              ) : null
            }
          >
            <div className="-mx-1 overflow-x-auto sm:mx-0 sm:px-0">
              <table className="w-full min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr
                    className={cn(
                      'border-b text-left text-xs font-semibold uppercase tracking-wider',
                      colors.border,
                      'bg-slate-50/95 dark:bg-slate-800/50',
                    )}
                  >
                    <th className="w-10 py-2.5 pl-1 pr-2 text-left tabular-nums sm:pl-0">#</th>
                    <th className="w-[10rem] min-w-[8rem] py-2.5 pl-0 pr-3">Level 1</th>
                    <th className="min-w-[12rem] py-2.5 pl-0 pr-3 sm:min-w-[16rem]">Item</th>
                    <th className="w-20 min-w-[4.5rem] py-2.5 pl-0 pr-3">Qty</th>
                    <th className="w-40 min-w-[9rem] py-2.5 pl-0 pr-3">Last Requested</th>
                    <th className="w-40 min-w-[9rem] py-2.5 pl-0 pr-3">Last Ordered</th>
                    <th className="w-10 py-2.5 pl-0 pr-1 text-center">Hist.</th>
                    <th className="min-w-[8rem] py-2.5 pl-0 pr-0 sm:pr-0">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(item.lines || []).length === 0 ? (
                    <tr>
                      <td colSpan={8} className={cn('py-8 text-center text-sm', colors.textSecondary)}>
                        No line items on this requisition.
                      </td>
                    </tr>
                  ) : (
                    (item.lines || []).map((line, lineIndex) => (
                      <tr
                        key={line.id ?? `${line.itemId}-${line.itemDescr}`}
                        className={cn(
                          'border-b text-left transition-colors last:border-0',
                          colors.border,
                          'hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                        )}
                      >
                        <td className={cn('py-2.5 pl-1 pr-2 text-left tabular-nums sm:pl-0', colors.textSecondary)}>{lineIndex + 1}</td>
                        <td className={cn('w-[10rem] min-w-[8rem] py-2.5 pl-0 pr-3 align-top', colors.text)}>
                          {(line.item?.level1 ?? '').trim() || ''}
                        </td>
                        <td className={cn('min-w-[12rem] py-2.5 pl-0 pr-3 align-top sm:min-w-[16rem]', colors.text)}>
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">{formatRequisitionItemCell(line)}</div>
                            {line.itemId != null && line.itemId > 0 ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                title="View item details"
                                aria-label="View item details"
                                className="h-8 w-8 shrink-0"
                                onClick={() => setViewItemId(line.itemId!)}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                        <td className={cn('w-20 py-2.5 pl-0 pr-3 align-top tabular-nums', colors.text)}>{line.qty ?? ''}</td>
                        <td className={cn('w-40 min-w-[9rem] py-2.5 pl-0 pr-3 align-top', colors.text)}>
                          {line.lastRequested ? (
                            <div className="leading-tight">
                              <div className="text-sm tabular-nums">
                                {line.lastRequested.reqDate ? formatDate(line.lastRequested.reqDate) : '—'}
                              </div>
                              <div className={cn('mt-0.5 text-xs tabular-nums', colors.textSecondary)}>
                                Qty: {line.lastRequested.qty ?? '—'}
                              </div>
                            </div>
                          ) : (
                            <span className={colors.textSecondary}>—</span>
                          )}
                        </td>
                        <td className={cn('w-40 min-w-[9rem] py-2.5 pl-0 pr-3 align-top', colors.text)}>
                          {line.lastOrdered ? (
                            <div className="leading-tight">
                              <div className="text-sm tabular-nums">
                                {line.lastOrdered.orderDate ? formatDate(line.lastOrdered.orderDate) : '—'}
                              </div>
                              <div className={cn('mt-0.5 text-xs tabular-nums', colors.textSecondary)}>
                                Qty: {line.lastOrdered.qty ?? '—'}
                              </div>
                            </div>
                          ) : (
                            <span className={colors.textSecondary}>—</span>
                          )}
                        </td>
                        <td className="w-10 py-2.5 pl-0 pr-1 text-center align-top">
                          <span className={cn('text-xs', colors.textSecondary)}>—</span>
                        </td>
                        <td className={cn('max-w-[20rem] py-2.5 pl-0 pr-0 align-top', colors.text)}>{line.itemNotes || ''}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>

      <SpareItemDetailModal
        isOpen={viewItemId != null}
        itemId={viewItemId}
        onClose={() => setViewItemId(null)}
      />

      <Modal isOpen={enquiryOpen} onClose={() => !enquiryBusy && setEnquiryOpen(false)} size="md" zIndex={110}>
        <ModalHeader>
          <ModalTitle>Create supplier enquiry</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <p className={cn('text-sm', colors.textSecondary)}>
              Lines and header will be copied from this requisition. Enter the supplier to send the enquiry to.
            </p>
            <div>
              <Label htmlFor="enq-from-req-supplier">Supplier</Label>
              <Select
                id="enq-from-req-supplier"
                name="enquirySupplierId"
                value={enquirySupplierId}
                onChange={(e) => setEnquirySupplierId(e.target.value)}
                options={supplierModalOptions}
                placeholder="Select supplier"
                showClearButton={false}
              />
            </div>
            <div>
              <Label htmlFor="enq-from-req-notes">Notes (optional)</Label>
              <Textarea
                id="enq-from-req-notes"
                value={enquiryNotes}
                onChange={(e) => setEnquiryNotes(e.target.value)}
                rows={3}
                showClearButton={false}
                placeholder="Override requisition notes on the new enquiry"
                className="min-h-[88px]"
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="ghost" size="sm" disabled={enquiryBusy} onClick={() => setEnquiryOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={enquiryBusy}
            disabled={!enquirySupplierId.trim() || Number.isNaN(Number(enquirySupplierId))}
            onClick={async () => {
              const supplierId = Number(enquirySupplierId);
              if (!Number.isFinite(supplierId)) return;
              setEnquiryBusy(true);
              try {
                const created = (await createSpareSupplierEnquiryFromRequisition({
                  requisitionId: item.id,
                  supplierId,
                  ...(enquiryNotes.trim() ? { notes: enquiryNotes.trim() } : {}),
                })) as { id?: number };
                setEnquiryOpen(false);
                if (created?.id) {
                  navigate(supplierEnquiryConfig.module.route.detail(created.id));
                }
              } catch (err: unknown) {
                const data =
                  err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                    : undefined;
                const m = data?.message;
                const text = Array.isArray(m) ? m.join('; ') : typeof m === 'string' ? m : 'Could not create supplier enquiry.';
                showError(text);
              } finally {
                setEnquiryBusy(false);
              }
            }}
          >
            Create
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Detail;
